import { Component, ViewChild, effect, signal, ChangeDetectionStrategy } from '@angular/core';

import { IEntityMetaData } from '@app/common-codes/_models/IEntityMetaData';
import { GenericListComponent } from '@app/common-codes/components/list-components/generic-base-list/generic-list.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { ListTableColumnComponent } from '@app/common-codes/components/list-components/list-table-column/list-table-column.component';
import { ListViewComponent } from '@app/common-codes/components/list-components/list-view/list-view.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Delivery } from '@app/common-codes/models/matwins-tenant/Delivery.model';
import { LookupProfile, LookupItem } from '@app/common-codes/services/LookupService';
import { DeliveryDetailComponent } from '../delivery-detail/delivery-detail.component';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { MultiCriteriaFilterComponent, FilterOption } from '@app/common-codes/components/nested-components/multi-criteria-filter/multi-criteria-filter.component';
import { IFilterOperator, IFilterCriteria } from '@app/common-codes/components/_primitives/IFilterCriteria';
import { MenuItemObject } from '@app/common-codes/components/models/context-menu.models';

@Register(Delivery)
@Component({
  selector: 'app-delivery-list',
  templateUrl: './delivery-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ListViewComponent,
    DeliveryDetailComponent,
    ListTableColumnComponent,
    TranslatePipe,
    ReactiveBadgeComponent,
    MultiCriteriaFilterComponent
]
})
export class DeliveryListComponent extends GenericListComponent<Delivery> {

  override Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  override ComponentEntityClass = Delivery;
  override entityMetaData: IEntityMetaData = new Delivery().GetType();
  override contextMenuItems: MenuItemObject<Delivery>[] = [
    {
      itemKey: 'notify',
      itemText: 'entities.delivery.propreties.numberOfNotificationSent',
      icon: 'fas fa-bell',
      action: (entity: Delivery) => this.notifyDelivery(entity),
      disabled: (entity: Delivery) => entity?.fK_Employee == null
    }
  ];
  modalEntity = signal<Delivery | null>(null);
  @ViewChild('deliveryDetail', { read: DeliveryDetailComponent }) deliveryDetail!: DeliveryDetailComponent;

  deliveryStatusLookup = signal<LookupItem[]>([]);
  /// Lookup avec valeurs traduites pour le reactive-badge
  translatedStatusLookup = signal<LookupItem[]>([]);
  filterOptions = signal<FilterOption[]>([]);

  public static override get lookupProfiles(): LookupProfile[] {
    return [
      { lookupName: "DeliveryStatus" },
      { lookupName: "PointOfService" },
      { lookupName: "TimeZone" },
      { lookupName: "Employee" },
    ];
  }

  constructor(_alertModalService: AlertModalService) {
    super();
    this.alertModalService = _alertModalService;

    effect(() => {
      const entities = this.listView()?.entities();
      if (entities) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        const raw = this.lookupService.getLookupData('DeliveryStatus');
        if (!raw || raw.length === 0) return;
        this.deliveryStatusLookup.set(raw);
        // Traduire les valeurs pour le reactive-badge
        this.translatedStatusLookup.set(raw.map(item => ({
          ...item,
          value: this.translationService.instant(`entities.delivery-status.${(item.value || '').toLowerCase().replace(/\s+/g, '-')}`)
        })));
        this.buildFilterOptions();
      }
    });
  }

  private buildFilterOptions(): void {
    const statusLookup = this.deliveryStatusLookup();
    if (!statusLookup || statusLookup.length === 0) return;

    const selectedIds = new Set(this.selectedFilterOptionIds());
    const options: FilterOption[] = statusLookup
      .filter(s => s.value?.toLowerCase() !== 'unknown')
      .map(s => ({
        id: `status_${s.key}`,
        label: this.translationService.instant(`entities.delivery-status.${(s.value || '').toLowerCase().replace(/\s+/g, '-')}`),
        fieldName: 'status',
        group: 'DeliveryStatus',
        isSelected: selectedIds.has(`status_${s.key}`)
      }));
    this.filterOptions.set(options);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  override getdetailComponent(): GenetricDetailComponent<Delivery> | undefined {
    return this.deliveryDetail;
  }

  onStatusChange(newStatus: number, entity: Delivery): void {
    this.updateEntityProperty(entity, 'status' as keyof Delivery, newStatus);
  }

  async notifyDelivery(entity: Delivery): Promise<void> {
    try {
      await this.dataService.execute('POST', 'sendNotification', { id: entity.id });
      entity.numberOfNotificationSent = (entity.numberOfNotificationSent || 0) + 1;
      this.onItemUpdated(entity);
    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  }
}
